import React, { useState, useEffect } from 'react'
import theme from '../../config/theme'
import Input from '../../common/input'
import { IoCloseOutline, IoEyeOutline, IoEyeOffOutline } from 'react-icons/io5'
import { userApi, ApiError } from '../../utils/apiClient'
import { useAuth } from '../../contexts/authContext'
import { validateUsername, validateEmailWithMessage, validatePasswordStrong } from '../../utils/validation'
import { getErrorMessage } from '../../utils/errorMessages'

export interface ModifyDialogProps {
  open: boolean
  userId?: number
  userEmail?: string
  mode: 'create' | 'edit'
  onClose: () => void
  onSuccess?: () => void
 }

const ModifyDialog: React.FC<ModifyDialogProps> = ({
  open,
  userId: initialUserId,
  userEmail,
  mode,
  onClose,
  onSuccess,
}) => {
  const { user: currentUser } = useAuth()
  const [userName, setUserName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Check if user is editing themselves
  const isSelfEdit = mode === 'edit' && currentUser?.email === userEmail

  // Initialize form data when opening modal
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && initialUserId) {
        // Fetch user data for edit mode
        setIsLoading(true)
        userApi.getUserById(initialUserId)
          .then((user) => {
            setUserName(user.username || user.userName || '')
            setEmail(user.email || '')
            setPassword('') // Don't pre-fill password
            setErrors({})
          })
          .catch((err) => {
            setErrors({ general: err instanceof Error ? err.message : 'Failed to load user data' })
          })
          .finally(() => {
            setIsLoading(false)
          })
      } else {
        // Reset form for create mode
        setUserName('')
        setEmail('')
        setPassword('')
        setErrors({})
      }
      setShowPassword(false)
    }
  }, [open, initialUserId, mode])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        onClose()
      }
    }
    if (open) {
      document.addEventListener('keydown', handleEscape)
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open, onClose])

  const validateField = (fieldName: string, value: string): string | undefined => {
    switch (fieldName) {
      case 'userName':
        const usernameResult = validateUsername(value)
        return usernameResult.error
      case 'email':
        const emailResult = validateEmailWithMessage(value)
        return emailResult.error
      case 'password':
        // Password is required for create, optional for edit
        const isRequired = mode === 'create'
        const passwordResult = validatePasswordStrong(value, isRequired)
        return passwordResult.error
      default:
        return undefined
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    const userNameError = validateField('userName', userName)
    if (userNameError) newErrors.userName = userNameError

    const emailError = validateField('email', email)
    if (emailError) newErrors.email = emailError

    const passwordError = validateField('password', password)
    if (passwordError) newErrors.password = passwordError

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate all fields and show errors
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    // Don't clear validation errors - they should remain visible
    // Only clear general errors if any
    setErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors.general
      return newErrors
    })

    try {
      // Don't trim password - validate as-is (spaces are not allowed)
      if (mode === 'create') {
        await userApi.createUser(userName.trim(), email.trim(), password)
      } else if (initialUserId) {
        // Only send password if it's provided in edit mode
        // Use original email if editing self (email field is disabled)
        const emailToUpdate = isSelfEdit ? userEmail || email : email.trim()
        await userApi.updateUser(
          initialUserId,
          userName.trim(),
          emailToUpdate,
          password || undefined
        )
      }
      
      // Success - close dialog and refresh user list
      handleClose()
      // Trigger refresh callback if provided
      if (onSuccess) {
        onSuccess()
      }
    } catch (err) {
      if (err instanceof ApiError) {
        // Handle field-level validation errors from server
        if (err.isValidationError() && err.fieldErrors) {
          setErrors(err.fieldErrors)
        } else {
          setErrors({ general: getErrorMessage(err) })
        }
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to save user'
        setErrors({ general: errorMessage })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setUserName('')
    setEmail('')
    setPassword('')
    setErrors({})
    onClose()
  }

  if (!open) return null

  const title = mode === 'create' ? 'Create New User' : 'Edit User'
  const description = mode === 'create' 
    ? 'Fill in the details below to create a new user account.' 
    : 'Edit in the details below.'
  const submitButtonText = mode === 'create' ? 'Create User' : 'Save'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 relative"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '500px' }}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 p-1 rounded transition-colors"
          style={{ color: theme.color.gray200 }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = theme.color.gray500
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = theme.color.gray200
          }}
        >
          {React.createElement(IoCloseOutline as any, {
            className: "w-6 h-6"
          })}
        </button>

        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <h2
            className="text-xl font-medium mb-2"
            style={{ color: theme.color.gray500 }}
          >
            {title}
          </h2>
          <p
            className="text-sm"
            style={{ color: theme.color.gray200 }}
          >
            {description}
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: theme.brandColor.red }}></div>
          </div>
        )}

        {/* Form */}
        {!isLoading && (
        <form onSubmit={handleSubmit} className="px-6 pb-6">
           {/* Username Field */}
           <div className="mb-4">
             <Input
               type="text"
               id="username"
               name="username"
               label="Username"
               
               value={userName}
               onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                 setUserName(e.target.value)
                 if (errors.userName) {
                   setErrors(prev => ({ ...prev, userName: '' }))
                 }
               }}
               onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                 const error = validateField('userName', e.target.value)
                 setErrors(prev => ({ ...prev, userName: error || '' }))
               }}
               placeholder="e.g. johndoe"
               error={!!errors.userName}
               errorMessage={errors.userName}
               isValid={!errors.userName && !!userName.trim()}
             />
           </div>

           {/* Email Field */}
           <div className="mb-4">
             <Input
               type="email"
               id="email"
               name="email"
               label="Email"
               
               value={email}
               onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                 if (!isSelfEdit) {
                   setEmail(e.target.value)
                   if (errors.email) {
                     setErrors(prev => ({ ...prev, email: '' }))
                   }
                 }
               }}
               onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                 if (!isSelfEdit) {
                   const error = validateField('email', e.target.value)
                   setErrors(prev => ({ ...prev, email: error || '' }))
                 }
               }}
               placeholder="e.g. john@example.com"
               error={!!errors.email}
               errorMessage={errors.email}
               isValid={!errors.email && !!email.trim()}
               disabled={isSelfEdit}
             />
           </div>

           {/* Password Field */}
           <div className="mb-6">
             <label
               htmlFor="password"
               className="block text-sm mb-2"
               style={{ color: theme.color.gray400 }}
             >
               Password
               {mode === 'create' && <span style={{ color: theme.brandColor.red }} className="ml-1">*</span>}
               {mode === 'edit' && <span className="ml-2 text-xs" style={{ color: theme.color.gray200 }}>(leave blank to keep current)</span>}
             </label>
             <div className="relative">
                 <Input
                   type={showPassword ? 'text' : 'password'}
                   id="password"
                   name="password"
                   value={password}
                   onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                     setPassword(e.target.value)
                     if (errors.password) {
                       setErrors(prev => ({ ...prev, password: '' }))
                     }
                   }}
                   onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                     const error = validateField('password', e.target.value)
                     setErrors(prev => ({ ...prev, password: error || '' }))
                   }}
                   placeholder={mode === 'edit' ? 'Enter new password (optional)' : 'Enter a secure password'}
                   error={!!errors.password}
                   errorMessage={errors.password}
                   isValid={!errors.password && (mode === 'edit' || password.trim().length > 0)}
                   className="pr-12"
                   
                 />
               <button
                 type="button"
                 onClick={() => setShowPassword(!showPassword)}
                 className="absolute right-4 top-1/2 transform -translate-y-1/2 p-1"
                 style={{ color: theme.color.gray200 }}
                 onMouseEnter={(e) => {
                   e.currentTarget.style.color = theme.color.gray400
                 }}
                 onMouseLeave={(e) => {
                   e.currentTarget.style.color = theme.color.gray200
                 }}
               >
                 {showPassword 
                   ? React.createElement(IoEyeOffOutline as any, { className: "w-5 h-5" })
                   : React.createElement(IoEyeOutline as any, { className: "w-5 h-5" })
                 }
               </button>
             </div>
           </div>

           {/* General Error Message */}
           {errors.general && (
             <div
               className="mb-4 p-3 rounded-lg text-sm"
               style={{
                 backgroundColor: `${theme.color.error}20`,
                 borderColor: `${theme.color.error}50`,
                 color: theme.color.error,
                 border: '1px solid'
               }}
             >
               {errors.general}
             </div>
           )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            {/* Cancel Button */}
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{
                backgroundColor: theme.color.light100,
                border: `1px solid ${theme.color.light500}`,
                color: theme.color.gray500
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.9'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1'
              }}
            >
              Cancel
            </button>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: theme.brandColor.red,
                color: theme.color.light100,
                border: 'none'
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting && !isLoading) {
                  e.currentTarget.style.opacity = '0.9'
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1'
              }}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {mode === 'create' ? 'Creating...' : 'Saving...'}
                </span>
              ) : (
                submitButtonText
              )}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  )
}

export default ModifyDialog

